import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Label,
  ToggleGroup,
  ToggleGroupItem,
} from '@design-system';
import { TRANSCRIPT_INSIGHTS_PROMPT } from '@shared/calculations/funding/projection/transcript/insightsPrompt';
import { TRANSCRIPT_REVIEW_PROMPT } from '@shared/calculations/funding/projection/transcript/reviewPrompt';
import { useTranscript } from '../hooks/useTranscript';

interface TranscriptDialogProps {
  projection: ClaimProjection;
  open: boolean;
  onClose: () => void;
}

// Which prompt (if any) leads the copied/displayed output.
type PromptVariant = 'none' | 'review' | 'insights';

const PROMPTS: Record<PromptVariant, string | null> = {
  none: null,
  review: TRANSCRIPT_REVIEW_PROMPT,
  insights: TRANSCRIPT_INSIGHTS_PROMPT,
};

// Engineering-internal: renders a projection as a copyable plain-text transcript
// for eyeballing the product mechanics (paste into a chat for review or to draw
// out consumer-facing policy insights). The transcript is PII/PHI-free. Gated
// behind the page's debug flag — not a customer-facing export.
export function TranscriptDialog({ projection, open, onClose }: TranscriptDialogProps) {
  const { transcript, copy } = useTranscript(projection);
  const [variant, setVariant] = useState<PromptVariant>('none');

  // When a prompt is selected it leads the output (separated by a rule) so the
  // whole thing is paste-ready into a chat.
  const output = useMemo(() => {
    const prompt = PROMPTS[variant];
    return prompt ? `${prompt}\n\n---\n\n${transcript}` : transcript;
  }, [variant, transcript]);

  return (
    <Dialog open={open} onOpenChange={next => (!next ? onClose() : undefined)}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-[1600px] flex-col overflow-hidden sm:max-w-[1600px]">
        <DialogHeader className="flex flex-row items-center justify-between gap-4">
          <DialogTitle>Projection transcript</DialogTitle>
          <div className="flex items-center gap-3">
            <Label className="text-sm font-normal text-gray-500">Prepend prompt</Label>
            <ToggleGroup
              type="single"
              value={variant}
              onValueChange={v => setVariant((v || 'none') as PromptVariant)}
              variant="outline"
              size="sm"
              aria-label="Prepend prompt"
            >
              <ToggleGroupItem value="none">None</ToggleGroupItem>
              <ToggleGroupItem value="review">Review</ToggleGroupItem>
              <ToggleGroupItem value="insights">Insights</ToggleGroupItem>
            </ToggleGroup>
            <Button variant="outline" size="sm" onClick={() => copy(output)}>
              Copy
            </Button>
          </div>
        </DialogHeader>
        <pre className="overflow-auto whitespace-pre rounded-md bg-gray-50 p-4 font-mono text-[11px] leading-snug text-gray-800">
          {output}
        </pre>
      </DialogContent>
    </Dialog>
  );
}
